import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, BACKEND_URL } from "../../lib/api";
import { logger } from "../../lib/logger";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Star } from "lucide-react";

const resolveImg = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

export default function AdminArtistOfWeek() {
  const [artists, setArtists] = useState([]);
  const [current, setCurrent] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [quote, setQuote] = useState("");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const loadCurrent = () =>
    api
      .get("/artist-of-the-week")
      .then(({ data }) => {
        setCurrent(data);
        setSelectedId(data?.artist?.id || "");
        setQuote(data?.quote || "");
      })
      .catch((err) => logger.error("AOTW load failed:", err));

  useEffect(() => {
    api
      .get("/artists")
      .then(({ data }) => setArtists(data))
      .catch((err) => logger.error("Artists load failed:", err));
    loadCurrent();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { artist_id: selectedId, quote };
      const { data } = await api.patch("/admin/artist-of-the-week", payload);
      setCurrent(data);
      toast.success(selectedId ? "Artist of the Week updated" : "Cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/artist-of-the-week", { artist_id: "" });
      setCurrent(data);
      setSelectedId("");
      setQuote("");
      toast.success("Artist of the Week cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not clear");
    } finally {
      setSaving(false);
    }
  };

  const filtered = artists.filter((a) =>
    a.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="admin-aotw">
      <p className="label-eyebrow text-[#1E3A8A]">— Editorial</p>
      <h1 className="font-serif-display text-5xl tracking-tighter font-medium mt-3 text-[#111]">
        Artist of the Week
      </h1>
      <p className="text-[#555] mt-3 max-w-xl">
        Choose one artist to feature in a full-bleed homepage section + the public spotlight page.
        Add an optional quote to set the editorial tone.
      </p>

      {current?.artist && (
        <div
          data-testid="aotw-current"
          className="mt-10 border border-[#1E3A8A]/30 bg-[#F4EFE6]/40 p-6 flex items-center gap-6 flex-wrap"
        >
          <img
            src={resolveImg(current.artist.avatar_url)}
            alt={current.artist.name}
            className="w-20 h-20 object-cover rounded-full border border-[#E5E0D8]"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <div className="flex-1 min-w-0">
            <p className="label-eyebrow text-[#1E3A8A]">— Currently featured</p>
            <p className="font-serif-display text-3xl mt-1 text-[#111]">{current.artist.name}</p>
            {current.quote && (
              <p className="font-serif-display italic text-sm text-[#333] mt-2 max-w-md">
                "{current.quote}"
              </p>
            )}
          </div>
          <Button
            onClick={clear}
            disabled={saving}
            data-testid="aotw-clear-btn"
            className="rounded-none bg-white text-[#D32F2F] border border-[#D32F2F] hover:bg-[#D32F2F] hover:text-white px-5 py-3 h-auto label-eyebrow"
          >
            Clear
          </Button>
        </div>
      )}

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <p className="label-eyebrow text-[#1E3A8A]">— Pick the artist</p>
          <Input
            data-testid="aotw-search"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mt-4 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
          />
          <div className="mt-4 max-h-[420px] overflow-y-auto border border-[#E5E0D8] bg-white">
            {filtered.length === 0 ? (
              <p className="p-6 label-eyebrow text-[#555]">No artists match.</p>
            ) : (
              filtered.map((a) => (
                <button
                  key={a.id}
                  data-testid={`aotw-pick-${a.id}`}
                  onClick={() => setSelectedId(a.id)}
                  className={`w-full flex items-center gap-3 p-3 text-left border-b border-[#E5E0D8] last:border-b-0 hover:bg-[#F4EFE6]/40 transition-colors ${
                    selectedId === a.id ? "bg-[#F4EFE6]/70" : ""
                  }`}
                >
                  <img
                    src={resolveImg(a.avatar_url) || "https://images.unsplash.com/photo-1500336624523-d727130c3328?w=120"}
                    alt={a.name}
                    className="w-12 h-12 object-cover rounded-full border border-[#E5E0D8] flex-shrink-0"
                    onError={(e) => {
                      e.currentTarget.src = "https://images.unsplash.com/photo-1500336624523-d727130c3328?w=120";
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif-display text-base text-[#111] truncate">{a.name}</p>
                    <p className="font-mono-aam text-xs text-[#555] mt-0.5">{a.country}</p>
                  </div>
                  {selectedId === a.id && (
                    <Star className="w-4 h-4 text-[#1E3A8A]" fill="#F4C430" strokeWidth={1.5} />
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        <div>
          <p className="label-eyebrow text-[#1E3A8A]">— Editorial quote</p>
          <Textarea
            data-testid="aotw-quote"
            placeholder="e.g. 'Painting is a way of listening to the ancestors.'"
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            rows={4}
            maxLength={400}
            className="mt-4 rounded-none border border-[#E5E0D8] bg-white focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
          />
          <p className="text-xs text-[#555] mt-2 font-mono-aam">{quote.length}/400</p>

          <Button
            onClick={save}
            disabled={saving || !selectedId}
            data-testid="aotw-save-btn"
            className="mt-6 w-full rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white py-5 h-auto label-eyebrow disabled:opacity-40"
          >
            {saving ? "Saving…" : "Publish Artist of the Week"}
          </Button>
        </div>
      </div>
    </div>
  );
}
